/** The drawn boundary's polygons and their GeoJSON form (boundary-editor design §4.3). */
import type { MultiPolygon, Position } from 'geojson';
import type { LngLat } from '@/planner/types';
import { localProjection, ringArea } from '@/features/reports/trees/geometry';

/** Open ring: the closing vertex is implied. */
export type Ring = LngLat[];
export interface DrawnPolygon {
  shell: Ring;
  holes: Ring[];
}

/** The backend's limits (backend/intake/drawn.py). */
export const MAX_POLYGONS = 100;
export const MAX_VERTICES = 10_000;

const closed = (r: Ring): Position[] => [...r, r[0]];

export function toMultiPolygon(ps: DrawnPolygon[]): MultiPolygon {
  return { type: 'MultiPolygon', coordinates: ps.map((p) => [closed(p.shell), ...p.holes.map(closed)]) };
}

export function openRing(ring: unknown): Ring | null {
  if (!Array.isArray(ring)) return null;
  const pts = ring
    .filter((p): p is number[] => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map((p) => [p[0], p[1]] as LngLat);
  const [a, b] = [pts[0], pts[pts.length - 1]];
  if (pts.length >= 2 && a[0] === b[0] && a[1] === b[1]) pts.pop();
  return pts.length >= 3 ? pts : null;
}

function polygonOf(rings: unknown): DrawnPolygon | null {
  if (!Array.isArray(rings)) return null;
  const shell = openRing(rings[0]);
  if (!shell) return null;
  return { shell, holes: rings.slice(1).map(openRing).filter((r): r is Ring => r !== null) };
}

/** Every polygon in a GeoJSON geometry, feature or collection; anything else is skipped. */
export function polygonsFromGeoJson(node: unknown): DrawnPolygon[] {
  if (!node || typeof node !== 'object') return [];
  const g = node as { type?: string; coordinates?: unknown; geometry?: unknown; features?: unknown[]; geometries?: unknown[] };
  switch (g.type) {
    case 'Polygon': {
      const p = polygonOf(g.coordinates);
      return p ? [p] : [];
    }
    case 'MultiPolygon':
      return Array.isArray(g.coordinates) ? g.coordinates.map(polygonOf).filter((p): p is DrawnPolygon => p !== null) : [];
    case 'Feature':
      return polygonsFromGeoJson(g.geometry);
    case 'FeatureCollection':
      return (g.features ?? []).flatMap(polygonsFromGeoJson);
    case 'GeometryCollection':
      return (g.geometries ?? []).flatMap(polygonsFromGeoJson);
    default:
      return [];
  }
}

export function vertexCount(ps: DrawnPolygon[]): number {
  return ps.reduce((n, p) => n + p.shell.length + p.holes.reduce((m, h) => m + h.length, 0), 0);
}

export function areaHa(ps: DrawnPolygon[]): number {
  const first = ps[0]?.shell[0];
  if (!first) return 0;
  const project = localProjection(first[0], first[1]);
  const area = (r: Ring) => ringArea(r.map(project));
  return ps.reduce((sum, p) => sum + area(p.shell) - p.holes.reduce((s, h) => s + area(h), 0), 0) / 10_000;
}
